"use client";

import type { EventFlightDetails } from "@/app/types/event";
import { getFlightSearchRequestToLocalStorage } from "@/app/utils/services/service/localStorageService";
import type {
  FlightClassOption,
  FlightSearchRequest,
  FlightType,
  FlightTypeOption,
  SearchValues,
} from "@/types/flights";
import dayjs from "dayjs";
import { first, last, uniqueId } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";

export const getFlightClassOptions = (
  t: ReturnType<typeof useTranslations>
): FlightClassOption[] => [
  { label: t("HomePageFlights.economy"), value: "economy" },
  { label: t("HomePageFlights.premiumEconomy"), value: "premium_economy" },
  { label: t("HomePageFlights.businessClass"), value: "business" },
  { label: t("HomePageFlights.firstClass"), value: "first" },
];

export const getFlightTypeOptions = (
  t: ReturnType<typeof useTranslations>
): FlightTypeOption[] => [
  { label: t("HomePageFlights.roundtrip"), value: "rounded" },
  { label: t("HomePageFlights.oneWay"), value: "one_way" },
  { label: t("HomePageFlights.multiCity"), value: "multi_city" },
];

export const useSearchFlights = (flightDetails?: EventFlightDetails) => {
  const locale = useLocale();

  const lastSearchRequest = getFlightSearchRequestToLocalStorage(locale);

  const [values, setValues] = useState<SearchValues>(() => {
    if (flightDetails) {
      return {
        flightClass: "economy",
        flightType: "rounded",
        adults: [{ age: 17, id: uniqueId() }],
        children: [],
        infants: [],
        flights: [
          {
            from: "",
            to: flightDetails.iataCode || "",
            departDate: flightDetails.checkIn
              ? dayjs(flightDetails.checkIn).format("YYYY-MM-DD")
              : "",
            returnDate: flightDetails.checkOut
              ? dayjs(flightDetails.checkOut).format("YYYY-MM-DD")
              : "",
            id: uniqueId(),
          },
        ],
      };
    } else if (lastSearchRequest) {
      return lastSearchRequest;
    } else {
      return {
        flightClass: "economy",
        flightType: "rounded",
        adults: [{ age: 17, id: uniqueId() }],
        children: [],
        infants: [],
        flights: [
          {
            from: "",
            to: "",
            departDate: "",
            returnDate: "",
            id: uniqueId(),
          },
        ],
      };
    }
  });

  useEffect(() => {
    if (flightDetails?.checkIn && flightDetails?.checkOut) {
      setValues((prev) => ({
        ...prev,
        flights: prev.flights.map((flight, index) =>
          index === 0
            ? {
                ...flight,
                to: flightDetails.iataCode || flight.to,
                departDate: dayjs(flightDetails.checkIn).format("YYYY-MM-DD"),
                returnDate: dayjs(flightDetails.checkOut).format("YYYY-MM-DD"),
              }
            : flight
        ),
      }));
    }
  }, [flightDetails?.checkIn]);

  const updateFlightField = (
    flightId: string,
    updatedFields: Partial<FlightSearchRequest>
  ) => {
    setValues((prev) => ({
      ...prev,
      flights: prev.flights.map((f) =>
        f.id === flightId ? { ...f, ...updatedFields } : f
      ),
    }));
  };

  const handleClearAll = () => {
    setValues((prev) => ({
      ...prev,
      flights: [
        {
          from: "",
          to: "",
          departDate: "",
          returnDate: "",
          id: uniqueId(),
        },
      ],
    }));
  };

  const handleChangeFlightType = (flightType: FlightType) => {
    setValues((prev) => {
      const prevFirst = first(prev.flights);
      const firstFlight = {
        id: uniqueId(), // preserve values, assign new id
        from: prevFirst?.from ?? "",
        to: prevFirst?.to ?? "",
        departDate: prevFirst?.departDate ?? "",
        returnDate: prevFirst?.returnDate ?? "",
      };

      const secondFlight = {
        from: "",
        to: "",
        departDate: "",
        returnDate: "",
        id: uniqueId(),
      };

      return {
        ...prev,
        flightType,
        flights:
          flightType === "multi_city"
            ? [firstFlight, secondFlight]
            : [firstFlight],
      };
    });
  };

  const handleAddDestination = () => {
    setValues((prev) => {
      const previousFlight = last(prev.flights);

      const nextFlight: FlightSearchRequest = {
        id: uniqueId(),
        departDate: "",
        from: previousFlight?.to ?? "",
        returnDate: "",
        to: "",
      };

      return {
        ...prev,
        flights: [...prev.flights, nextFlight],
      };
    });
  };

  const handleRemoveDestination = (id: string) => {
    setValues((prev) => {
      const indexToRemove = prev.flights.findIndex(
        (flight) => flight.id === id
      );

      if (indexToRemove === -1) {
        return prev;
      }

      const updatedFlights = prev.flights.filter((flight) => flight.id !== id);

      if (indexToRemove > 0 && indexToRemove < prev.flights.length - 1) {
        const previous = prev.flights[indexToRemove - 1];
        const next = updatedFlights[indexToRemove];

        updatedFlights[indexToRemove] = {
          ...next,
          from: previous.to,
        };
      }

      return { ...prev, flights: updatedFlights };
    });
  };

  const buildFlightSearchPayload = () => {
    const { flightType, flightClass, flights, adults, children, infants } =
      values;

    // Handle passengers
    const passengers = [];

    if (adults && adults.length > 0) {
      for (let i = 0; i < adults.length; i++) {
        passengers.push({ type: "adult" });
      }
    }

    if (children && children.length > 0) {
      passengers.push(...children.map((child) => ({ age: child.age })));
    }

    if (infants && infants.length > 0) {
      passengers.push(...infants.map((infant) => ({ age: infant.age })));
    }

    // Handle route data
    let payload: any = {
      tripType: flightType,
      cabinClass: flightClass,
      passengers,
    };

    if (flightType === "multi_city") {
      payload.citySlices = flights.map((flight) => ({
        origin: flight.from,
        destination: flight.to,
        departureDate: flight.departDate,
      }));
    } else {
      const flight = flights[0];
      payload.origin = flight.from;
      payload.destination = flight.to;
      payload.departureDate = flight.departDate;
      if (flightType === "rounded") {
        payload.returnDate = flight.returnDate;
      } else {
        delete payload.returnDate;
      }
    }

    return JSON.stringify(payload, null, 2);
  };

  return {
    handleRemoveDestination,
    handleAddDestination,
    updateFlightField,
    values,
    handleChangeFlightType,
    handleClearAll,
    setValues,
    buildFlightSearchPayload,
  };
};
